import {
  Alert,
  Button,
  ButtonGroup,
  IconButton,
  closeCurrentDialog,
  copyToClipboard,
  openDialog,
} from 'platform/components';
import {Box, HStack, Text, VStack} from 'platform/foundation';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

const SERVICE_ACCOUNT_KEY_RESULT_DIALOG_ID = 'serviceAccountKeyResultDialog';

interface ServiceAccountKeyResultDialogContentProps extends RequiredTestIdProps {
  keyId: string;
  privateKeyPem: string;
}

function ServiceAccountKeyResultDialogContent(props: ServiceAccountKeyResultDialogContentProps) {
  const privateKeyLines = props.privateKeyPem.split('\n');

  return (
    <VStack spacing={4}>
      <Alert
        variant="warning"
        title={i18n.t('entity.serviceAccount.labels.privateKeyWarningTitle')}
        message={i18n.t('entity.serviceAccount.labels.privateKeyWarningDescription')}
        data-testid={suffixTestId('warning', props)}
      />

      <VStack spacing={1}>
        <Text size="small" color="tertiary">
          {i18n.t('entity.serviceAccount.labels.keyId')}
        </Text>
        <HStack spacing={2} align="center" justify="space-between">
          <Text size="small" data-testid={suffixTestId('keyId', props)}>
            {props.keyId}
          </Text>
          <IconButton
            icon="content/copy"
            priority="secondary"
            onClick={() => copyToClipboard(props.keyId)}
            aria-label={i18n.t('general.actions.copyToClipboard')}
            data-testid={suffixTestId('copyKeyId', props)}
          />
        </HStack>
      </VStack>

      <VStack spacing={1}>
        <Text size="small" color="tertiary">
          {i18n.t('entity.serviceAccount.labels.privateKey')}
        </Text>
        <Box backgroundColor="palettes.neutral.20.100" borderRadius="small" padding={3}>
          <HStack spacing={2} align="flex-start" justify="space-between">
            <VStack spacing={0}>
              {privateKeyLines.map((line, index) => (
                <Text key={`${index}-${line}`} size="small">
                  {line}
                </Text>
              ))}
            </VStack>
            <IconButton
              icon="content/copy"
              priority="secondary"
              onClick={() => copyToClipboard(props.privateKeyPem)}
              aria-label={i18n.t('general.actions.copyToClipboard')}
              data-testid={suffixTestId('copyPrivateKey', props)}
            />
          </HStack>
        </Box>
        <Text size="small" color="tertiary">
          {i18n.t('entity.serviceAccount.labels.publicKeyAvailabilityHint')}
        </Text>
      </VStack>

      <ButtonGroup align="right">
        <Button
          variant="primary"
          onClick={closeCurrentDialog}
          title={i18n.t('entity.serviceAccount.actions.privateKeySaved')}
          data-testid={suffixTestId('confirm', props)}
        />
      </ButtonGroup>
    </VStack>
  );
}

interface OpenServiceAccountKeyResultDialogArgs {
  title: string;
  keyId: string;
  privateKeyPem: string;
  // Called once the dialog has been fully dismissed. The create flow uses this to
  // navigate only after the private key has been acknowledged — navigating while the
  // dialog is open would close it, since the dialog manager clears dialogs on route change.
  onClose?: () => void;
}

export function openServiceAccountKeyResultDialog(args: OpenServiceAccountKeyResultDialogArgs) {
  openDialog(
    <ServiceAccountKeyResultDialogContent
      keyId={args.keyId}
      privateKeyPem={args.privateKeyPem}
      data-testid={SERVICE_ACCOUNT_KEY_RESULT_DIALOG_ID}
    />,
    {
      id: SERVICE_ACCOUNT_KEY_RESULT_DIALOG_ID,
      title: args.title,
      size: 'large',
      scrollBehavior: 'outside',
      withAdditionalFooter: false,
      onCloseComplete: args.onClose,
      'data-testid': SERVICE_ACCOUNT_KEY_RESULT_DIALOG_ID,
    }
  );
}
